import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, updateDoc } from 'firebase/firestore';
import { getDoc } from '../../utils/firestoreRead';
import { db } from '../../firebase/firebaseConfig';
import InvitacionRender, { getNameBottomPct, numberFromPercent, INVITACION_DEFAULT_LAYOUT } from './InvitacionRender';
import EntradaRender from './EntradaRender';
import './CalibradorArte.css';

const INVITACION_DEFAULT = INVITACION_DEFAULT_LAYOUT;

const ENTRADA_DEFAULT = {
  qrTop: 53,
  qrLeft: 50,
  qrSize: 260,
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
const percentText = (value, fallback = 50) => `${numberFromPercent(value, fallback).toFixed(1)}%`;

function normalizarRuta(raw) {
  if (!raw) return '';
  if (raw.startsWith('http://') || raw.startsWith('https://')) return raw;
  if (raw.startsWith('/')) return raw;
  if (raw.startsWith('eventos/')) return `/${raw}`;
  return `/eventos/${raw}`;
}

function normalizarLayoutEntrada(layout) {
  if (!layout || typeof layout !== 'object') return ENTRADA_DEFAULT;
  return {
    qrTop: typeof layout.qrTop === 'number' ? layout.qrTop : typeof layout.top === 'number' ? layout.top : ENTRADA_DEFAULT.qrTop,
    qrLeft: typeof layout.qrLeft === 'number' ? layout.qrLeft : typeof layout.left === 'number' ? layout.left : ENTRADA_DEFAULT.qrLeft,
    qrSize: typeof layout.qrSize === 'number' ? layout.qrSize : typeof layout.size === 'number' ? layout.size : ENTRADA_DEFAULT.qrSize,
  };
}

function normalizarLayoutInvitacion(layout) {
  return { ...INVITACION_DEFAULT, ...(layout || {}) };
}

export default function CalibradorArte({ tipo = 'invitacion', eventoBorrador, onLayoutChange, ajustePantalla=false }) {
  const { eventoId } = useParams();
  const navigate = useNavigate();
  const frameRef = useRef(null);
  const dragRef = useRef(null);
  const dragCleanup=useRef(null);
  const stageRef=useRef(null);
  const [fitWidth,setFitWidth]=useState(300);
  const [frameWidth,setFrameWidth]=useState(300);

  const esEntrada = tipo === 'entrada';
  const inline=!!eventoBorrador;
  const [evento, setEvento] = useState(eventoBorrador||null);
  const [layout, setLayout] = useState(esEntrada ? normalizarLayoutEntrada(eventoBorrador?.layoutEntrada) : normalizarLayoutInvitacion(eventoBorrador?.layoutInvitacion));
  const [cargando, setCargando] = useState(!inline);
  const changeRef=useRef(onLayoutChange);changeRef.current=onLayoutChange;
  useEffect(()=>{if(inline)changeRef.current?.(layout);},[layout,inline]);
  const [guardando, setGuardando] = useState(false);
  const [mensaje, setMensaje] = useState('');
  const [arteAspectRatio, setArteAspectRatio] = useState('9 / 16');


  useEffect(() => {
    if(inline){setEvento(eventoBorrador);setCargando(false);return;}
    const cargar = async () => {
      try {
        const ref = doc(db, 'eventos', eventoId);
        const snap = await getDoc(ref);
        if (!snap.exists()) {
          setMensaje('El evento no existe.');
          setEvento(null);
          return;
        }
        const data = snap.data();
        setEvento({ id: snap.id, ...data });
        setLayout(esEntrada ? normalizarLayoutEntrada(data.layoutEntrada) : normalizarLayoutInvitacion(data.layoutInvitacion));
      } catch (error) {
        console.error('Error cargando evento para calibración', error);
        setMensaje('No se pudo cargar el evento.');
      } finally {
        setCargando(false);
      }
    };
    cargar();
  }, [eventoId, esEntrada, inline, eventoBorrador]);

  const arteRuta = useMemo(() => {
    if (!evento) return '';
    if (esEntrada) return normalizarRuta(evento.arteEntrada || evento.arteEntrada);
    return normalizarRuta(evento.arteInvitacion);
  }, [evento, esEntrada]);

  const titulo = esEntrada ? 'Calibrar entrada' : 'Calibrar invitación';
  const descripcion = esEntrada
    ? 'Ajusta la posición y tamaño del QR de la entrada.'
    : 'Ajusta el QR y el nombre de prueba sobre el arte de invitación.';

  const qrTopPct = numberFromPercent(layout.qrTop, esEntrada ? 53 : 50.5);
  const qrLeftPct = numberFromPercent(layout.qrLeft, esEntrada ? 50 : 51.5);
  const nameLeftPct = numberFromPercent(layout.nameLeft, 51.5);
  const ratioParts=arteAspectRatio.split('/').map(Number);
  const logicalHeight=300*ratioParts[1]/ratioParts[0];
  const previewScale=ajustePantalla?frameWidth/300:1;
  const nameBottomPct = getNameBottomPct(layout, logicalHeight);
  const nameBottomPxVisual = (nameBottomPct / 100) * logicalHeight;

  const patchLayout = (patch) => setLayout((prev) => ({ ...prev, ...patch }));

  const actualizarMedidasFrame = () => {
    const rect = frameRef.current?.getBoundingClientRect();

    if (rect?.width) setFrameWidth(rect.width);
  };

  useEffect(()=>{
    if(!ajustePantalla||!stageRef.current)return;
    const update=()=>{const r=stageRef.current.getBoundingClientRect();setFitWidth(Math.max(1,Math.min(r.width-4,(r.height-4)*ratioParts[0]/ratioParts[1])));};
    update();const observer=new ResizeObserver(update);observer.observe(stageRef.current);
    return()=>observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[ajustePantalla,arteAspectRatio]);
  useEffect(()=>{actualizarMedidasFrame();},[fitWidth]);
  useEffect(()=>()=>dragCleanup.current?.(),[]);

  useEffect(() => {
    actualizarMedidasFrame();
    window.addEventListener('resize', actualizarMedidasFrame);
    return () => window.removeEventListener('resize', actualizarMedidasFrame);
  }, [arteAspectRatio]);

  const handleArteLoad = (event) => {
    const img = event.currentTarget;
    if (img?.naturalWidth && img?.naturalHeight) {
      setArteAspectRatio(`${img.naturalWidth} / ${img.naturalHeight}`);
      requestAnimationFrame(actualizarMedidasFrame);
    }
  };

  const startDrag = (elemento) => (event) => {
    event.preventDefault();
    dragCleanup.current?.();
    dragCleanup.current=stopDrag;
    dragRef.current = elemento;
    window.addEventListener('pointermove', onPointerMove, { passive: false });
    window.addEventListener('pointerup', stopDrag);
    window.addEventListener('pointercancel', stopDrag);
  };

  const stopDrag = () => {
    dragRef.current = null;
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', stopDrag);
    window.removeEventListener('pointercancel', stopDrag);
  };

  const onPointerMove = (event) => {
    if (!dragRef.current) return;
    event.preventDefault();
    const rect = frameRef.current?.getBoundingClientRect();
    if (!rect) return;

    const x = clamp(event.clientX - rect.left, 0, rect.width);
    const y = clamp(event.clientY - rect.top, 0, rect.height);
    const leftPct = clamp((x / rect.width) * 100, 0, 100);
    const topPct = clamp((y / rect.height) * 100, 0, 100);

    if (dragRef.current === 'qr') {
      if (esEntrada) patchLayout({ qrLeft: Number(leftPct.toFixed(1)), qrTop: Number(topPct.toFixed(1)) });
      else patchLayout({ qrLeft: `${leftPct.toFixed(1)}%`, qrTop: `${topPct.toFixed(1)}%` });
    }

    if (dragRef.current === 'name' && !esEntrada) {
      const bottomPct = clamp(((rect.height - y) / rect.height) * 100, 0, 100);
      patchLayout({
        nameLeft: `${leftPct.toFixed(1)}%`,
        nameBottomPct: `${bottomPct.toFixed(2)}%`,
        nameBottomPx: Number((bottomPct * logicalHeight / 100).toFixed(1)),
      });
    }
  };

  const centrarQR = () => {
    if (esEntrada) patchLayout({ qrTop: 50, qrLeft: 50 });
    else patchLayout({ qrTop: '50.0%', qrLeft: '50.0%' });
  };

  const centrarNombre = () => patchLayout({ nameLeft: '50.0%' });
  const restablecer = () => setLayout(esEntrada ? ENTRADA_DEFAULT : INVITACION_DEFAULT);

  const guardar = async () => {
    if (!evento) return;
    setGuardando(true);
    setMensaje('');
    try {
      const payload = esEntrada
        ? {
            layoutEntrada: {
              qrTop: Number(qrTopPct),
              qrLeft: Number(qrLeftPct),
              qrSize: Number(layout.qrSize || ENTRADA_DEFAULT.qrSize),
            },
          }
        : {
            layoutInvitacion: {
              ...layout,
              nameBottomPct: `${nameBottomPct.toFixed(2)}%`,
              nameBottomPx: Number(nameBottomPxVisual.toFixed(1)),
            },
          };

      await updateDoc(doc(db, 'eventos', evento.id), payload);
      setMensaje('Calibración guardada correctamente ✅');
    } catch (error) {
      console.error('Error guardando calibración', error);
      setMensaje('No se pudo guardar. Revisa conexión y permisos.');
    } finally {
      setGuardando(false);
    }
  };

  if (cargando) {
    return <div className="cal-page cal-center">Cargando evento...</div>;
  }

  if (!evento) {
    return <div className="cal-page cal-center">{mensaje || 'No se encontró el evento.'}</div>;
  }

  return (
    <main className="cal-page">
      <header className="cal-header">
        <div>
          <p className="cal-eyebrow">{esEntrada ? 'Entradas' : 'Invitaciones'}</p>
          <h1>{titulo}</h1>
          <p>{descripcion}</p>
          <div className="cal-event-meta">
            <span>{evento.nombre || 'Evento sin nombre'}</span>
            {evento.fechaTexto && <span>{evento.fechaTexto}</span>}
            {evento.empresaNombre && <span>{evento.empresaNombre}</span>}
          </div>
        </div>
        {!inline&&<button type="button" className="cal-btn cal-btn-secondary" onClick={() => navigate('/eventos')}>
          Volver
        </button>}
      </header>

      <section className="cal-shell">
        <section className="cal-preview-card">
          <div className="cal-card-title-row">
            <div>
              <h2>Vista previa</h2>
              <p>Arrastra con el dedo o mouse. Usa los controles para ajustar fino.</p>
            </div>
            <span className="cal-badge">{esEntrada ? 'Entrada' : 'Invitación'}</span>
          </div>

          {!arteRuta ? (
            <div className="cal-empty">
              Este evento no tiene arte de {esEntrada ? 'entrada' : 'invitación'} asignado.
            </div>
          ) : (
            <div className="cal-phone-stage" ref={stageRef}>
              <div ref={frameRef} className="cal-art-frame" style={{ aspectRatio: arteAspectRatio,...(ajustePantalla?{width:fitWidth,flex:'none'}:{}) }}>
                {esEntrada ? (
                  <>
                    <div style={ajustePantalla?{width:300,transform:`scale(${previewScale})`,transformOrigin:'top left'}:undefined}><EntradaRender
                      fondo={arteRuta}
                      id="PREVIEW123"
                      layout={layout}
                      width="100%"
                      showGuides
                      onImageLoad={handleArteLoad}
                    /></div>

                    <button
                      type="button"
                      className="cal-hotspot cal-hotspot-qr"
                      style={{
                        top: `${qrTopPct}%`,
                        left: `${qrLeftPct}%`,
                        width: Number(layout.qrSize || 260)*previewScale,
                        height: Number(layout.qrSize || 260)*previewScale,
                      }}
                      onPointerDown={startDrag('qr')}
                      aria-label="Mover QR"
                    />
                  </>
                ) : (
                  <>
                    <div style={ajustePantalla?{width:300,transform:`scale(${previewScale})`,transformOrigin:'top left'}:undefined}><InvitacionRender
                      fondo={arteRuta}
                      codigo="PREVIEW123"
                      nombre="NOMBRE DE PRUEBA"
                      onNamePointerDown={startDrag('name')}
                      layout={layout}
                      width="100%"
                      showGuides
                      onImageLoad={handleArteLoad}
                    /></div>

                    <button
                      type="button"
                      className="cal-hotspot cal-hotspot-qr"
                      style={{
                        top: `${qrTopPct}%`,
                        left: `${qrLeftPct}%`,
                        width: (Number(layout.qrSize || 130) + 12)*previewScale,
                        height: (Number(layout.qrSize || 130) + 12)*previewScale,
                      }}
                      onPointerDown={startDrag('qr')}
                      aria-label="Mover QR"
                    />

                  </>
                )}
              </div>
            </div>
          )}
        </section>

        <aside className="cal-controls-card">
          <div className="cal-card-title-row compact">
            <div>
              <h2>Configuración</h2>
              <p>Valores guardados por evento.</p>
            </div>
          </div>

          <div className="cal-control-group">
            <div className="cal-group-head">
              <strong>QR</strong>
              <button type="button" onClick={centrarQR}>Centrar QR</button>
            </div>

            <label>
              <span>Vertical</span>
              <b>{qrTopPct.toFixed(1)}%</b>
              <input type="range" min="0" max="100" step="0.1" value={qrTopPct} onChange={(e) => patchLayout({ qrTop: esEntrada ? Number(e.target.value) : `${e.target.value}%` })} />
            </label>

            <label>
              <span>Horizontal</span>
              <b>{qrLeftPct.toFixed(1)}%</b>
              <input type="range" min="0" max="100" step="0.1" value={qrLeftPct} onChange={(e) => patchLayout({ qrLeft: esEntrada ? Number(e.target.value) : `${e.target.value}%` })} />
            </label>

            <label>
              <span>Tamaño</span>
              <b>{Number(layout.qrSize || 0).toFixed(0)} px</b>
              <input type="range" min={esEntrada ? 120 : 60} max={esEntrada ? 340 : 220} step="1" value={Number(layout.qrSize || (esEntrada ? 260 : 130))} onChange={(e) => patchLayout({ qrSize: Number(e.target.value) })} />
            </label>
          </div>

          {!esEntrada && (
            <div className="cal-control-group">
              <div className="cal-group-head">
                <strong>Nombre</strong>
                <button type="button" onClick={centrarNombre}>Centrar nombre</button>
              </div>

              <label>
                <span>Altura desde abajo</span>
                <b>{nameBottomPct.toFixed(1)}%</b>
                <input type="range" min="0" max="100" step="0.1" value={nameBottomPct} onChange={(e) => { const v = Number(e.target.value); patchLayout({ nameBottomPct: `${v.toFixed(2)}%`, nameBottomPx: Number(((v / 100) * logicalHeight).toFixed(1)) }); }} />
              </label>

              <label>
                <span>Horizontal</span>
                <b>{nameLeftPct.toFixed(1)}%</b>
                <input type="range" min="0" max="100" step="0.1" value={nameLeftPct} onChange={(e) => patchLayout({ nameLeft: `${e.target.value}%` })} />
              </label>

              <label>
                <span>Tamaño texto</span>
                <b>{Number(layout.nameFontSize || 12)} px</b>
                <input type="range" min="8" max="32" step="1" value={Number(layout.nameFontSize || 12)} onChange={(e) => patchLayout({ nameFontSize: Number(e.target.value) })} />
              </label>

              <label className="cal-color-label">
                <span>Color</span>
                <input type="color" value={layout.nameColor || '#000000'} onChange={(e) => patchLayout({ nameColor: e.target.value })} />
              </label>
            </div>
          )}

          {mensaje && <div className="cal-message">{mensaje}</div>}

          <div className="cal-actions">
            <button type="button" className="cal-btn cal-btn-ghost" onClick={restablecer}>Restablecer</button>
            {!inline&&<button type="button" className="cal-btn cal-btn-primary" onClick={guardar} disabled={guardando || !arteRuta}>
              {guardando ? 'Guardando...' : 'Guardar calibración'}
            </button>}
          </div>
        </aside>
      </section>
    </main>
  );
}

